import { useParams, useSearchParams, Outlet } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { LoadingPage, LoadingFailedPage } from "~/components/loading";
import { Stretcher } from "~/components/Stretcher/Stretcher";
import {
  SpaceOrWorkspaceScope,
  SpaceScope,
  ScopeType,
  isSpaceOrWorkspaceScope,
} from "~/hooks/useScope";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { setDocumentTitle } from "~/lib/commonUtils";
import { getInitiativeDetailUrl } from "~/lib/urlUtils";
import {
  useGetInitiativesQuery,
  InitiativeType,
  InitiativeStatus,
  Initiative,
  GetInitiativesDocument,
  GetCasesCountDocument,
} from "~/operations";
import {
  TabNavigation,
  TabListItem,
  useRouteMatch,
} from "~/components/tab-nav";
import { InventoryPageHeader } from "~/routes";
import { FindingSubheader } from "~/pages/findings/FindingOnAsset/utils";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { useDialogs } from "@toolpad/core";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { useTickets } from "~/pages/tickets/hooks/useTickets";

interface InitiativeDetailPageProps {
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
}

export function InitiativeDetailPage({
  scope,
  spaceScope,
}: InitiativeDetailPageProps) {
  const { initiativeMrn } = useParams<{ initiativeMrn: string }>();
  const [searchParams] = useSearchParams();
  const { isFeatureEnabled } = useFeatureFlags();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();
  const { hasTicketingIntegration } = useTicketingIntegrations();
  const dialogs = useDialogs();

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createCaseLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const { data, loading, error } = useGetInitiativesQuery({
    variables: {
      input: {
        entityMrn: scope.mrn,
        type: InitiativeType.Aggregate,
        mrns: [initiativeMrn || ""],
      },
    },
    skip: !initiativeMrn || !scope.mrn,
  });

  if (loading) {
    return <LoadingPage what="initiative" />;
  }

  if (error) {
    return <LoadingFailedPage what="initiative" />;
  }

  const initiative: Initiative | undefined =
    data?.initiatives?.edges?.[0]?.node;

  const isSecurityPipelineEnabled =
    isFeatureEnabled("Security Pipeline") &&
    hasSecurityPipelineIntegrations &&
    initiative?.status === InitiativeStatus.NotStarted;

  const isTicketDisabled =
    !spaceScope ||
    !scope ||
    !isSpaceOrWorkspaceScope(scope) ||
    !hasCreateTicketPermissions;

  const metadata =
    initiative?.metadata?.__typename === "AggregateInitiativeMetadata"
      ? initiative?.metadata
      : null;

  const progress = metadata?.assetCount
    ? ((metadata?.fixedCount || 0) / metadata?.assetCount) * 100
    : 0;

  const generateHref = (tab: string): string => {
    if (!initiative?.mrn) return "";

    const baseUrl = getInitiativeDetailUrl({
      initiativeMrn: initiative.mrn,
      scopeParams: searchParams,
    });

    // Insert tab path before the query string
    const [path, query] = baseUrl.split("?");
    return `${path}/${tab}${query ? `?${query}` : ""}`;
  };

  const tabList: TabListItem[] = [
    {
      label: "Remediations",
      to: generateHref("remediations"),
      route: "/remediations",
    },
    {
      label: "Findings",
      to: generateHref("findings"),
      route: "/findings",
    },
    {
      label: "Assets",
      to: generateHref("assets"),
      route: "/assets",
    },
  ];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "remediations",
  );

  const subheaders: FindingSubheader[] = [
    {
      id: "progress",
      label: "Progress",
      value: undefined,
      component: (
        <Typography variant="subtitle2" className="info-title">
          Progress{" "}
          <Typography component="span" variant="subtitle2">
            {`${progress.toFixed(0)}%`}
          </Typography>
        </Typography>
      ),
    },
    {
      id: "asset-count",
      label: "Assets",
      value: undefined,
      component: (
        <Typography variant="subtitle2" className="info-title">
          Assets{" "}
          <Typography component="span" variant="subtitle2">
            {String(metadata?.assetCount || 0)}
          </Typography>
        </Typography>
      ),
    },
    {
      id: "last-updated",
      label: "Last updated",
      value: String(initiative?.updatedAt || ""),
    },
  ];

  const fallbackPath =
    scope.type === ScopeType.Space
      ? `/space/overview?spaceId=${spaceScope.id}`
      : `/space/dashboard?spaceId=${spaceScope.id}`;

  const handleSecurityPipelinePRDialogOpen = () => {
    return dialogs.open(CreateSecurityPipelinePrDialog, {
      initiatives: initiative ? [initiative] : [],
      spaceScope,
    });
  };

  const handleCreateTicketDialogOpen = () => {
    if (!spaceScope || !isSpaceOrWorkspaceScope(scope)) return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: createCaseLoading,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetInitiativesDocument],
        }),
      selectedEntities: [
        {
          mrn: initiative?.mrn || "",
          scopeMrn: scope?.mrn || "",
        },
      ],
      space: spaceScope,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      isInitiative: true,
    });
  };

  setDocumentTitle([initiative?.title, "Initiative"]);

  return (
    <>
      <Stretcher>
        <DetailPageTopSection
          content={{
            triageBar: {
              title: initiative?.title || "",
              fallbackPath,
              subheaders,
              isLoading: loading,
              triage: true,
              availableRemediations: {
                ticket: {
                  active: true,
                  onClick: hasTicketingIntegration
                    ? handleCreateTicketDialogOpen
                    : handleCreateTicketingIntegrationModalOpen,
                  disabled: isTicketDisabled,
                },
                exception: { active: false },
                securityPipeline: {
                  active: isSecurityPipelineEnabled,
                  onClick: handleSecurityPipelinePRDialogOpen,
                },
              },
            },
            stats: [],
            right: <Box />,
            initiative,
          }}
        />
      </Stretcher>
      <InventoryPageHeader>
        <Box>
          <TabNavigation
            id="initiative-tabs"
            tabList={tabList}
            currentTab={currentTab}
          />
          <Outlet
            context={{
              initiative,
              scope,
              spaceScope,
            }}
          />
        </Box>
      </InventoryPageHeader>
    </>
  );
}
